import {StartConversation} from '../conversations/StartConversation'
import {usePersistedDiffCommentData} from '../../hooks/use-persisted-comment-data'
import {InlineMarkers} from '../conversations/InlineMarkers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {NoEntryIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type React from 'react'
import {forwardRef, memo, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {copyCode} from '../../helpers/copy-code'
import {useMarkersDialogContext} from '../../contexts/MarkersDialogContext'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  groupDiffLines,
  isContextDiffLine,
  isEmptyDiffLine,
  lineAcceptsComments,
  lineNeedsMarkerPadding,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import {useActionBarDialogs} from '../../hooks/use-action-bar-dialogs'
import {useStartConversationDialog} from '../../hooks/use-start-conversation-dialog'
import {useActionBarFocus} from '../../hooks/use-action-bar-focus'
import {useSuggestedChanges} from '../../hooks/use-suggested-changes'
import type {ClientDiffLine, FileDiffReference, DiffLineWithSelectionInfo} from '../../types'
import {ActionBar} from './ActionBar'
import CommentIndicator from './CommentIndicator'
import {InProgressCommentIndicator} from '../InProgressCommentIndicator'
import DiffLineScreenReaderSummary from '../DiffLineScreenReaderSummary'
import styles from './ContentCell.module.css'
import type {MarkersStatusState, MarkersStatusAction} from '../../hooks/use-markers-dialog-status'
import {
  useAddStartNewCommentOnLine,
  useDialogModeActions,
  useDialogModeIsEnabled,
  useDiffLinesActions,
  useGetDiffLinesInfo,
  useGetFirstLineSelectionOnDrag,
  useGetSelectedDiffLines,
  useGetSelectedLineRange,
  useTreeEntries,
} from '../../stores/selectors'
import {handleLineSelectionEffects} from '../../helpers/line-selection'
import {diffLineKey} from '../../stores/slices/helpers/diff-lines-slice-helpers'
import type {HunkData} from '../../helpers/hunk-data-helpers'
import {Cell} from './Cell'
import {useCommentDialogTitle} from '../../hooks/use-comment-dialog-title'
import {useEnterDialogMode} from '../../hooks/use-enter-dialog-mode'
import {useCreateReviewCommentMutation} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {useSelectedRefContext} from '@github-ui/pull-requests/contexts/SelectedRefContext'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useComparisonInfo} from '@github-ui/pull-requests/pull-request-store-selectors'
import {ReviewCommentFactory} from '../../helpers/review-comment-factory'
import {reviewCommentIsMultiline} from '../../helpers/review-comment-is-multiline'
import type {DiffAnnotation, Thread, ViewerData} from '../../conversation-types'
import {VALUES} from '../../constants'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import {DiffAddCommentButtonClickedEvent} from '../../util/custom-events'
import {registerPointerExitObserver} from './shared-pointer-exit-tracker'

function lineHasThreads(totalThreadAndAnnotationCount: number) {
  return totalThreadAndAnnotationCount !== 0
}

interface ContentCellProps extends React.ComponentProps<'td'> {
  annotationsAtPosition: DiffAnnotation[]
  columnIndex: number
  commentIndicator?: React.ReactElement | null
  commentsPreference: CommentsPreference | undefined
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  dispatchMarkersStatus: React.Dispatch<MarkersStatusAction>
  filePath: string
  handleDiffSideCellSelectionBlocking: (event: React.MouseEvent) => void
  isLeftSide: boolean
  isRowSelected: boolean
  isSplit: boolean
  lineAnchor?: string
  markersStatus: MarkersStatusState
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  rowId: string
  subjectId: string
  threadsAtPosition: Thread[]
  viewerData: ViewerData
}

/**
 * Renders the diff text.
 * Optionally renders comment indicator and action menu, if commenting is enabled.
 */

const ContentCellUnmemoized = forwardRef<HTMLTableCellElement, ContentCellProps>(function ContentCellUnmemoized(
  {
    annotationsAtPosition,
    columnIndex,
    commentsPreference,
    copilotChatReference,
    currentHunk,
    diffAnchor,
    diffLine,
    dispatchMarkersStatus,
    filePath,
    handleDiffSideCellSelectionBlocking,
    isLeftSide,
    isRowSelected,
    isSplit,
    lineAnchor,
    markersStatus,
    nextHunk,
    previousHunk,
    rowId,
    subjectId,
    threadsAtPosition,
    viewerData,
  }: ContentCellProps,
  forwardedRef,
) {
  const line = diffLine as DiffLineWithSelectionInfo

  const [lineHtml, lineTypeCharacter] = trimDiffLineHtml(line.html, line.type)
  const showLineTypeCharacter = lineTypeCharacter && ['+', '-'].includes(lineTypeCharacter)

  const [manuallyUpdateCommentsWithThisThreadId, setManuallyUpdateComments] = useState('')
  const {isActionBarVisible, hideActionBar, anyMenuOpen} = useMarkersDialogContext()
  const moveNewCommentEnabled = useFeatureFlag('move_new_comments_ui')

  const {hasPersistedComment} = usePersistedDiffCommentData({
    diffSide: isLeftSide ? 'LEFT' : 'RIGHT',
    filePath,
    line: line.blobLineNumber,
    subjectId,
    fileLevelComment: false,
  })

  const viewerDataToPass = {...viewerData, commentsPreference}

  const cellId = cellIdFrom(rowId, columnIndex)
  const isInDialogMode = useDialogModeIsEnabled(cellId)
  const {disableDialogMode} = useDialogModeActions()
  const lineSide = isLeftSide ? 'LEFT' : 'RIGHT'
  const lineNumber = lineSide === 'RIGHT' ? line.right : line.left
  const lineMarkersKey = getDiffLineMarkersKey(lineNumber, lineSide)
  const threadCount = threadsAtPosition.length
  const annotationCount = annotationsAtPosition.length

  // When a user closes a dialog launched from either the action bar or the context menu, we want to return focus to the
  // element that triggered the dialog.
  const actionBarReturnFocusRef = useRef<HTMLButtonElement | null>(null)
  const cellRef = useRef<HTMLTableCellElement>(null)

  // delegate the internal ref to the forwarded one if there is one
  useImperativeHandle<HTMLTableCellElement | null, HTMLTableCellElement | null>(forwardedRef, () => cellRef.current, [])

  const hasThreads = lineHasThreads(threadCount + annotationCount)

  const commentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasThreads && commentsPreference === CommentsPreference.Collapsed,
  })

  const addCommentDialogTitle = useCommentDialogTitle(line, !!isLeftSide, isRowSelected)

  const {
    handleCellBlur,
    handleCellFocus,
    handleCellMouseEnter: handleCellMouseEnterForActionBarFocus,
    handleCellMouseLeave: handleCellMouseLeaveForActionBarFocus,
  } = useActionBarFocus({
    cellRef,
  })

  useEffect(() => {
    if (!isActionBarVisible || !cellRef.current) return

    const cellContainsFocus = () => {
      const activeElement = ssrSafeDocument?.activeElement
      const cellElement = cellRef.current
      if (!cellElement || !activeElement) return false
      return cellElement === activeElement || cellElement.contains(activeElement)
    }

    return registerPointerExitObserver({
      element: cellRef.current,
      shouldIgnore: () => anyMenuOpen || cellContainsFocus(),
      onPointerExit: hideActionBar,
    })
  }, [anyMenuOpen, hideActionBar, isActionBarVisible])

  const inlineMarkersRef = useRef<HTMLDivElement>(null)

  const enterDialogMode = useEnterDialogMode(cellId, cellRef, dispatchMarkersStatus)

  const getDiffLinesInfo = useGetDiffLinesInfo()
  const getSelectedDiffLines = useGetSelectedDiffLines()
  const addStartNewCommentOnLine = useAddStartNewCommentOnLine()
  const getSelectedLineRange = useGetSelectedLineRange()
  const commentOutsideOfTheDiffEnabled = useFeatureFlag('cotd_ui')

  const closeFocusMode = useCallback(
    (shouldFocus = true) => {
      // A setTimeout is needed to ensure that the focus is restored to the cellRef after the dialog is closed, otherwise focus might be lost
      if (shouldFocus) {
        requestAnimationFrame(() => cellRef.current?.focus())
      }
      disableDialogMode()
      dispatchMarkersStatus('USER_MINIMIZED_MARKERS')
    },
    [disableDialogMode, dispatchMarkersStatus],
  )

  const toggleViewingMarkers = useCallback(() => {
    if (commentsPreference === CommentsPreference.Collapsed && markersStatus.showMarkers) {
      closeFocusMode()
    } else {
      enterDialogMode()
    }
  }, [closeFocusMode, enterDialogMode, markersStatus.showMarkers, commentsPreference])

  const {closeMarkerListDialog, returnFocusRef, isContextMenuOpen} = useActionBarDialogs({
    cellId,
    actionBarRef: actionBarReturnFocusRef,
  })
  const openDialogFunction = useCallback(() => {
    enterDialogMode()
    if (!viewerData.shouldDisplayComments) {
      hideActionBar()
    }
    let selectedLineRange = getSelectedLineRange()
    if (!selectedLineRange) {
      // if there is no selected line range, that means it was a single line comment, so pass that info in
      selectedLineRange = {
        startLineNumber: line.blobLineNumber,
        endLineNumber: line.blobLineNumber,
        startOrientation: isLeftSide ? 'left' : 'right',
        endOrientation: isLeftSide ? 'left' : 'right',
        diffAnchor,
      }
    }
    const addCommentEvent = new DiffAddCommentButtonClickedEvent(selectedLineRange, filePath)
    ssrSafeWindow?.dispatchEvent(addCommentEvent)
  }, [
    enterDialogMode,
    viewerData.shouldDisplayComments,
    getSelectedLineRange,
    filePath,
    hideActionBar,
    line.blobLineNumber,
    isLeftSide,
    diffAnchor,
  ])
  const {
    isNewConversationDialogOpen,
    shouldStartNewConversationWithSuggestedChange,
    startNewConversation,
    closeNewConversation,
  } = useStartConversationDialog({
    cellId,
    onOpenDialog: openDialogFunction,
  })

  const showStartConversation: boolean = useMemo(() => {
    if (!isActionBarVisible) return false
    if (!viewerData.shouldDisplayComments) {
      // if commenting is disabled, we want to show the + button all of the time even after clicking it once.
      if (commentOutsideOfTheDiffEnabled) {
        return !isEmptyDiffLine(line)
      } else {
        return lineAcceptsComments(line, groupDiffLines(getSelectedDiffLines()))
      }
    }
    if (isNewConversationDialogOpen) return false
    if (!viewerData.viewerCanComment) return false

    if (commentOutsideOfTheDiffEnabled) {
      return !isEmptyDiffLine(line)
    } else {
      return lineAcceptsComments(line, groupDiffLines(getSelectedDiffLines()))
    }
  }, [
    isActionBarVisible,
    isNewConversationDialogOpen,
    viewerData.shouldDisplayComments,
    viewerData.viewerCanComment,
    commentOutsideOfTheDiffEnabled,
    line,
    getSelectedDiffLines,
  ])

  // TODO: remove this use of useSelectedRefContext when we remove :cotd_ui Feature Flag
  const {endOid, startOid} = useSelectedRefContext()
  const {mutate: createReviewCommentMutation} = useCreateReviewCommentMutation()
  const {effectiveRange: effectiveDiffRange} = useComparisonInfo()
  const {newTreeEntry, oldTreeEntry} = useTreeEntries()

  const handleAddComment = ({
    onCompleted,
    onError,
    submitBatch,
    text,
  }: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => {
    if (!line) return

    const selectedLineRange = getSelectedLineRange()

    if (commentOutsideOfTheDiffEnabled) {
      const requestOpts = {
        filePath,
        newTreeEntry,
        oldTreeEntry,
        effectiveDiffRange,
        submitBatch,
        text,
      }
      let requestData
      const reviewCommentFactory = new ReviewCommentFactory(requestOpts)

      if (selectedLineRange && reviewCommentIsMultiline(isRowSelected, selectedLineRange))
        requestData = reviewCommentFactory.multilineRequestData(selectedLineRange)
      else {
        requestData = reviewCommentFactory.lineRequestData(line, isLeftSide)
      }

      createReviewCommentMutation(requestData, {
        onError,
        onSuccess: data => {
          onCompleted?.(data.thread.id, data.comment?.databaseId)
          enterDialogMode()
          setManuallyUpdateComments(data.thread.id)
        },
      })
    } else {
      const isMultiLineComment: boolean =
        selectedLineRange && selectedLineRange.startLineNumber !== selectedLineRange.endLineNumber ? true : false
      let endLine: number = line.blobLineNumber
      let side: 'left' | 'right' = isLeftSide && !isContextDiffLine(line) ? 'left' : 'right'
      let startLine: number | undefined
      let startSide: 'left' | 'right' | undefined

      if (selectedLineRange && isMultiLineComment && isRowSelected) {
        side = selectedLineRange.endOrientation === 'left' ? 'left' : 'right'
        startSide = selectedLineRange.startOrientation === 'left' ? 'left' : 'right'
        endLine = selectedLineRange.endLineNumber
        startLine = selectedLineRange.startLineNumber
      }

      createReviewCommentMutation(
        {
          text,
          line: endLine,
          path: filePath,
          side,
          startSide,
          submitBatch,
          startLine,
          comparisonEndOid: endOid,
          comparisonStartOid: startOid,
        },
        {
          onError,
          onSuccess: data => {
            onCompleted?.(data.thread.id, data.comment?.databaseId)
            enterDialogMode()
            setManuallyUpdateComments(data.thread.id)
          },
        },
      )
    }
  }

  const handleDeleteLineComment = () => {
    if (threadsAtPosition.length <= 1) {
      setTimeout(() => cellRef.current?.focus())
    }
  }

  const suggestedChangesConfig = useSuggestedChanges(line, shouldStartNewConversationWithSuggestedChange)

  const handleStartNewConversation = useCallback(() => {
    enterDialogMode?.()
    startNewConversation?.()
  }, [enterDialogMode, startNewConversation])

  useEffect(() => {
    // add the function which opens a new comment on this particular content cell to the map so that
    // it can be called from any arbitrary line
    if (moveNewCommentEnabled) {
      addStartNewCommentOnLine(lineMarkersKey, handleStartNewConversation, diffLineKey(line))
    }
  }, [addStartNewCommentOnLine, handleStartNewConversation, line, lineMarkersKey, moveNewCommentEnabled])

  const shouldAnimate = useRef(true)

  const showInlineComments = useMemo(() => {
    if (!viewerData.shouldDisplayComments) return false
    if (!commentsPreference) return false
    if (hasThreads && commentsPreference === CommentsPreference.Collapsed) {
      return !markersStatus.userMinimized
    }

    if (!isNewConversationDialogOpen && !hasThreads) return false

    if (commentsPreference === CommentsPreference.Collapsed) {
      if (isNewConversationDialogOpen) return true
      if (isInDialogMode) return true
    }

    if (commentsPreference === CommentsPreference.Visible) {
      if (hasThreads) return true
      if (isNewConversationDialogOpen) return true
      if (markersStatus.showMarkers) return true
    }

    return false
  }, [
    hasThreads,
    isInDialogMode,
    isNewConversationDialogOpen,
    markersStatus.showMarkers,
    markersStatus.userMinimized,
    commentsPreference,
    viewerData.shouldDisplayComments,
  ])

  const lineHasMarkerPadding = useMemo(() => lineNeedsMarkerPadding(line), [line])
  const {addLineToSelection, clearSelectedLines, selectLine} = useDiffLinesActions()
  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()

  useEffect(() => {
    if (hasPersistedComment && commentsPreference === CommentsPreference.Visible) {
      handleStartNewConversation()
    }
  }, [hasPersistedComment, handleStartNewConversation, commentsPreference])

  const isLastSelectedLine = useCallback(() => {
    const lineRange = getSelectedLineRange()

    if (!lineRange) {
      return false
    }

    const lineIsOnRightSide = lineRange.endOrientation === 'right' && !isLeftSide
    if (lineIsOnRightSide && lineRange.endLineNumber === line.right) {
      return true
    }

    const lineIsOnLeftSide = lineRange && lineRange.endOrientation === 'left' && isLeftSide
    if (lineIsOnLeftSide && lineRange.endLineNumber === line.left) {
      return true
    }

    return false
  }, [getSelectedLineRange, isLeftSide, line.left, line.right])

  const handleMouseDownAddCommentButton = useCallback(() => {
    // Clears line selection when user clicks on add comment button on any line but the last one selected.
    if (!isLastSelectedLine()) {
      clearSelectedLines()
    }
  }, [clearSelectedLines, isLastSelectedLine])

  const handleMouseLeaveAddCommentButton = useCallback(
    (e: React.MouseEvent) => {
      // only handle line selection if mouse is being held down.
      if (e.buttons !== 1) return

      // We need to call this so that the + Add comment button doesn't stay rendered on the top of the drag line
      handleCellMouseLeaveForActionBarFocus()

      const lineNumberToSelect = isLeftSide ? line.left : line.right
      const lineKey = diffLineKey(line)
      if (!isRowSelected && lineNumberToSelect && !isLastSelectedLine()) {
        selectLine(
          {
            lineKey,
            firstLineSelectionOnDrag: {
              lineNumber: lineNumberToSelect,
              orientation: isLeftSide ? 'left' : 'right',
              lineKey,
              shouldStartConversation: true,
            },
          },
          handleLineSelectionEffects,
        )
      } else {
        addLineToSelection(
          {
            lineKey: diffLineKey(line),
            orientation: isLeftSide ? 'left' : 'right',
          },
          handleLineSelectionEffects,
        )
      }
    },
    [
      addLineToSelection,
      handleCellMouseLeaveForActionBarFocus,
      isLastSelectedLine,
      isLeftSide,
      isRowSelected,
      line,
      selectLine,
    ],
  )

  const handleMouseEnterCell = useCallback(
    (e: React.MouseEvent) => {
      const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
      if (e.buttons === 1 && firstLineSelectionOnDrag) {
        addLineToSelection(
          {
            lineKey: diffLineKey(line),
            orientation: isLeftSide ? 'left' : 'right',
          },
          handleLineSelectionEffects,
        )
      }

      handleCellMouseEnterForActionBarFocus()
    },
    [addLineToSelection, getfirstLineSelectionOnDrag, handleCellMouseEnterForActionBarFocus, isLeftSide, line],
  )

  const handleMouseLeaveCell = useCallback(() => {
    const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
    if (!firstLineSelectionOnDrag) {
      if (!viewerData.shouldDisplayComments && !isContextMenuOpen) {
        hideActionBar()
      }
      handleCellMouseLeaveForActionBarFocus()
      return
    }
    const selectedLineRange = getSelectedLineRange()
    const rightSideLineIsSelected =
      selectedLineRange?.startOrientation === 'right' &&
      firstLineSelectionOnDrag.orientation === 'right' &&
      !isLeftSide &&
      line.right === firstLineSelectionOnDrag.lineNumber

    const leftSideLineIsSelected =
      selectedLineRange?.startOrientation === 'left' &&
      firstLineSelectionOnDrag.orientation === 'left' &&
      isLeftSide &&
      line.left === firstLineSelectionOnDrag.lineNumber

    if (!rightSideLineIsSelected && !leftSideLineIsSelected) {
      handleCellMouseLeaveForActionBarFocus()
    }
  }, [
    getfirstLineSelectionOnDrag,
    getSelectedLineRange,
    isLeftSide,
    line.right,
    line.left,
    viewerData.shouldDisplayComments,
    isContextMenuOpen,
    handleCellMouseLeaveForActionBarFocus,
    hideActionBar,
  ])

  const handleExitDialogMode = useCallback(() => {
    dispatchMarkersStatus('USER_EXITED_MARKERS_DIALOG')
  }, [dispatchMarkersStatus])

  const handleUserClosedMarkersDialog = useCallback(() => {
    dispatchMarkersStatus('USER_MINIMIZED_MARKERS')
  }, [dispatchMarkersStatus])

  const onCloseCommentDialog = useCallback(
    (shouldFocusCell = true) => {
      closeFocusMode(shouldFocusCell)
      closeNewConversation()
    },
    [closeFocusMode, closeNewConversation],
  )

  const handleCopyCode = async () => {
    const diffLines = getDiffLinesInfo()
    await copyCode({
      diffLine,
      diffLines,
      getSelectedDiffLines,
      getSelectedLineRange,
      isSplit,
      isLeftSide,
    })
  }

  const key = `ContentCell-left:${line.left}-right:${line.right}-leftSide:${isLeftSide}`
  const inlineDialogHeadingId = `inline-dialog-heading-${useId()}`

  const startConversationLineNumber = useMemo(() => {
    if (!isRowSelected) return line.blobLineNumber

    const selectedLineRange = getSelectedLineRange()
    return selectedLineRange?.endLineNumber ?? line.blobLineNumber
  }, [getSelectedLineRange, isRowSelected, line.blobLineNumber])

  const startConversationStartLineNumber = useMemo(() => {
    if (!isRowSelected) return

    const selectedLineRange = getSelectedLineRange()
    return selectedLineRange?.startLineNumber
  }, [getSelectedLineRange, isRowSelected])

  return (
    <Cell
      key={key}
      ref={cellRef}
      cellId={cellId}
      columnIndex={columnIndex}
      commentDialogOpen={isNewConversationDialogOpen}
      diffAnchor={diffAnchor}
      diffLine={diffLine}
      enterDialogMode={enterDialogMode}
      handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
      handleExitDialogMode={handleExitDialogMode}
      handleUserClosedMarkersDialog={handleUserClosedMarkersDialog}
      lineAnchor={lineAnchor}
      handleStartConversation={handleStartNewConversation}
      hasThreads={hasThreads}
      isLeftSide={isLeftSide}
      isRowSelected={isRowSelected}
      isSplit={isSplit}
      className={clsx(
        `diff-text-cell ${isLeftSide ? 'left-side-diff-cell' : 'right-side-diff-cell'} ${
          lineHasMarkerPadding ? 'pt-4' : ''
        }`,
        {
          'border-right': isLeftSide && line.type !== 'HUNK',
        },
      )}
      style={{
        backgroundColor: getLineBackgroundColor(line.type, false, isRowSelected),
        paddingRight: commentIndicatorGutterSize,
      }}
      onBlur={handleCellBlur}
      onFocus={handleCellFocus}
      onMouseEnter={handleMouseEnterCell}
      onMouseLeave={handleMouseLeaveCell}
      aria-labelledby={isInDialogMode ? inlineDialogHeadingId : undefined}
      role={isInDialogMode ? 'dialog' : 'gridcell'}
    >
      {isInDialogMode && (
        <h1 id={inlineDialogHeadingId} className="sr-only">
          Comment view
        </h1>
      )}
      <code
        className={clsx('diff-text syntax-highlighted-line', {
          addition: line.type === 'ADDITION',
          deletion: line.type === 'DELETION',
        })}
      >
        {showLineTypeCharacter && <span className="diff-text-marker">{lineTypeCharacter}</span>}
        {/* Explicitly mark html as safe because it is server-sanitized */}
        <VerifiedHTMLDiv
          html={lineHtml as SafeHTMLString}
          className={clsx('diff-text-inner', {
            'color-fg-muted': line.type === 'HUNK',
          })}
        />
      </code>
      {line.displayNoNewLineWarning && <NoEntryIcon size={16} className="fgColor-danger" />}
      {(isActionBarVisible ||
        (line.isLastSelected &&
          isLeftSide &&
          getSelectedLineRange()?.endOrientation === 'left' &&
          line.left === getSelectedLineRange()?.endLineNumber) ||
        (!isLeftSide &&
          getSelectedLineRange()?.endOrientation === 'right' &&
          line.right === getSelectedLineRange()?.endLineNumber)) && (
        <ActionBar
          authorAvatarUrl={viewerData.avatarUrl}
          authorLogin={viewerData.login}
          ref={actionBarReturnFocusRef}
          cellId={cellId}
          cellRef={cellRef}
          copilotChatReference={copilotChatReference}
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          threads={threadsAtPosition}
          annotations={annotationsAtPosition}
          enterDialogMode={openDialogFunction}
          handleCopyCode={handleCopyCode}
          hasDraftComment={hasPersistedComment}
          isLeftSide={isLeftSide}
          isSplit={isSplit}
          nextHunk={nextHunk}
          onMouseDownAddCommentButton={handleMouseDownAddCommentButton}
          onMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
          previousHunk={previousHunk}
          showStartConversation={showStartConversation}
          shouldDisplayMinimizedComments={viewerData.shouldDisplayComments}
          shouldDisplayCollapseComments={markersStatus.showMarkers}
          toggleViewingMarkers={toggleViewingMarkers}
          viewerData={viewerDataToPass}
        />
      )}
      {showInlineComments ? (
        <InlineMarkers
          backgroundColor={getLineBackgroundColor(line.type, true, isRowSelected)}
          gridCellId={cellId}
          gutterSizeOffset={commentIndicatorGutterSize}
          inlineMarkersRef={inlineMarkersRef}
          cellRef={cellRef}
          dispatchMarkersStatus={dispatchMarkersStatus}
          lineMarkersKey={lineMarkersKey}
          manuallyUpdateCommentsWithThisThreadId={manuallyUpdateCommentsWithThisThreadId}
          annotations={annotationsAtPosition}
          batchingEnabled
          conversationListThreads={threadsAtPosition}
          fileAnchor={diffAnchor}
          filePath={filePath}
          onCloseFocusMode={closeFocusMode}
          isMarkerListOpen={
            // eslint-disable-next-line react-hooks/refs
            cellRef.current?.classList.contains('diff-text-cell') && threadsAtPosition.length > 0 ? true : false
          }
          isRowSelected={isRowSelected}
          lineType={line.type}
          returnFocusRef={returnFocusRef}
          subjectId={subjectId}
          suggestedChangesConfig={suggestedChangesConfig}
          onCloseConversationList={closeMarkerListDialog}
          onDeleteLineComment={handleDeleteLineComment}
          viewerData={viewerDataToPass}
          ghostUser={VALUES.ghostUser}
        >
          {isNewConversationDialogOpen && (
            <div
              className={clsx(
                ' border rounded-2 color-border-default',
                threadsAtPosition.length === 0 ? 'mt-2 mb-1' : 'mt-2 mb-1',
              )}
            >
              <StartConversation
                addCommentDialogTitle={addCommentDialogTitle}
                anchorRef={cellRef}
                diffAnchor={diffAnchor}
                batchingEnabled
                filePath={filePath}
                focusOnMount={!(hasPersistedComment && commentsPreference === CommentsPreference.Visible)}
                isLeftSide={!!isLeftSide}
                isInDialogMode={isInDialogMode}
                isOpen={isNewConversationDialogOpen}
                lineNumber={startConversationLineNumber}
                returnFocusRef={returnFocusRef}
                startLineNumber={startConversationStartLineNumber}
                subjectId={subjectId}
                suggestedChangesConfig={suggestedChangesConfig}
                viewerData={viewerDataToPass}
                onAddComment={handleAddComment}
                onCloseCommentDialog={onCloseCommentDialog}
              />
            </div>
          )}
        </InlineMarkers>
      ) : null}
      {!isActionBarVisible &&
        commentsPreference === CommentsPreference.Collapsed &&
        viewerData.shouldDisplayComments && (
          <>
            <div
              aria-hidden="true"
              style={{left: viewerData.lineSpacingPreference === 'compact' ? '-8px' : '-9px'}}
              className={clsx(
                'position-absolute top-0 d-flex user-select-none',
                styles['in-progress-comment-indicator'],
              )}
            >
              {hasPersistedComment && (
                <InProgressCommentIndicator
                  lineSpacingPreference={viewerData.lineSpacingPreference}
                  authorAvatarUrl={viewerData.avatarUrl}
                  authorLogin={viewerData.login}
                />
              )}
            </div>
            <div
              aria-hidden="true"
              className={clsx('position-absolute top-0 d-flex user-select-none', styles['comment-indicator'])}
            >
              <CommentIndicator
                threads={threadsAtPosition}
                annotations={annotationsAtPosition}
                shouldAnimateRef={shouldAnimate}
                lineSpacingPreference={viewerData.lineSpacingPreference}
              />
            </div>
          </>
        )}
      <DiffLineScreenReaderSummary annotationCount={annotationCount} threadCount={threadCount} />
    </Cell>
  )
})

export const ContentCell = memo(ContentCellUnmemoized)

try{ ContentCellUnmemoized.displayName ||= 'ContentCellUnmemoized' } catch {}
try{ ContentCell.displayName ||= 'ContentCell' } catch {}